import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { parseDate } from '@internationalized/date';
import { ApplyPendingFiltersCallback, CalendarDateFacet } from '../../../../types/search';
import { CALENDAR_DATE_FACET, CALENDAR_RESULTS_DATE_FACET } from '../..';
import { useCalendarViewMode } from '../../hooks/useCalendarViewMode';
import { useScheduleSummary } from '../../hooks/useScheduleSummary';
import CardStackSkeleton from './CardStackSkeleton';
import DateCard from './DateCard';

type CalendarCardStackProps = Readonly<{
  applyPendingFilters: ApplyPendingFiltersCallback;
  facetGroups: Record<string, unknown>;
  appliedFilters: Record<string, unknown>;
  calendarDateFacet: CalendarDateFacet;
}>;

export default function CalendarCardStack({ applyPendingFilters, calendarDateFacet }: CalendarCardStackProps) {
  const selectedValue = calendarDateFacet?.selectedValue || '';
  const mode = useCalendarViewMode(selectedValue);
  const cardStackRef = useRef<HTMLDivElement>(null);

  /**
   * Scroll reset when switching view (Weekly / Monthly)
   */
  useEffect(() => {
    if (mode === 'Custom') return;
    cardStackRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
  }, [mode]);

  /**
   * Apply filters from URL ONCE on initial load
   * Canonical source:
   *  - jobDate=from,to
   *  - status=<facetValueGuid>
   */
  useEffect(() => {
    const qs = new URLSearchParams(globalThis.location.search);

    const jobDate = qs.get('jobDate'); // "YYYY-MM-DD,YYYY-MM-DD"
    const status = qs.get('status'); // facet GUID

    if (!jobDate || !jobDate.includes(',')) return;

    const [from, to] = jobDate.split(',') as [string, string];

    applyPendingFilters({
      [CALENDAR_DATE_FACET]: [from, to],
      [CALENDAR_RESULTS_DATE_FACET]: null,
      ...(status ? { status: [status] } : {}),
    });
  }, [applyPendingFilters]);

  /**
   * Selected range derived from listing facet (jobDate)
   */
  const selectedDateRange = useMemo<[string, string]>(() => {
    if (!selectedValue.includes(',')) return ['', ''];
    return selectedValue.split(',') as [string, string];
  }, [selectedValue]);

  /**
   * Used by Weekly "All week" card
   */
  const getFocusDateFromUrl = useCallback((): string | null => {
    const qs = new URLSearchParams(globalThis.location.search);
    return qs.get(CALENDAR_RESULTS_DATE_FACET);
  }, []);

  const handleAllWeekClick = useCallback(() => {
    const [weekStart, weekEnd] = selectedDateRange;
    if (!weekStart || !weekEnd) return;

    applyPendingFilters({
      [CALENDAR_DATE_FACET]: [weekStart, weekEnd],
      [CALENDAR_RESULTS_DATE_FACET]: null,
    });
  }, [applyPendingFilters, selectedDateRange]);

  const isAllWeekSelected = useCallback(() => {
    return getFocusDateFromUrl() === null;
  }, [getFocusDateFromUrl]);

  /**
   * Status for schedule-summary (GUID)
   */
  const statusFromUrl = useMemo(() => {
    const urlParams = new URLSearchParams(globalThis.location.search);
    return urlParams.get('status') ?? undefined;
  }, []);

  /**
   * Schedule summary (calendar buckets)
   */
  const {
    data: dateRange,
    isLoading: isScheduleLoading,
    isError: isScheduleError,
  } = useScheduleSummary({
    view: mode,
    calendarDateFrom: selectedDateRange[0] ?? '',
    calendarDateTo: selectedDateRange[1] ?? '',
    status: statusFromUrl,
  });

  const handleDateCardClick = useCallback(
    (date: string) => {
      if (mode === 'Weekly') {
        applyPendingFilters({
          [CALENDAR_RESULTS_DATE_FACET]: date,
        });
        return;
      }

      // Monthly → select whole month
      const d = new Date(date);
      const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
      const lastDate = lastDay.toISOString().split('T')[0];

      applyPendingFilters({
        [CALENDAR_DATE_FACET]: [date, lastDate],
        [CALENDAR_RESULTS_DATE_FACET]: null,
      });
    },
    [applyPendingFilters, mode]
  );

  const isDateSelected = useCallback(
    (cardDate: string) => {
      if (!selectedValue.includes(',')) return false;

      const [from] = selectedValue.split(',');
      return mode === 'Monthly' ? cardDate === from : (getFocusDateFromUrl()?.startsWith(cardDate) ?? false);
    },
    [mode, selectedValue, getFocusDateFromUrl]
  );

  const totalJobsInRange = useMemo(() => {
    if (!dateRange) return 0;
    return dateRange.buckets.reduce((sum, b) => sum + b.total, 0);
  }, [dateRange]);

  if (mode === 'Custom') {
    return <div className="calendar-view__cards--no-results" />;
  }

  const renderContent = () => {
    if (isScheduleLoading) return <CardStackSkeleton />;
    if (isScheduleError) return <p>There was an error loading this data</p>;

    if (!dateRange?.buckets.length) return null;

    return (
      <>
        {mode === 'Weekly' && (
          <AllDaysCard totalJobs={totalJobsInRange} isSelected={isAllWeekSelected()} onClick={handleAllWeekClick} />
        )}

        <div className="calendar-view__cards">
          {dateRange.buckets.map(({ date, total }) => (
            <DateCard
              key={date}
              date={parseDate(date).toDate('UTC')}
              type={mode}
              isSelected={isDateSelected(date)}
              jobCount={total}
              onClick={() => {
                if (mode === 'Monthly' || total > 0) {
                  handleDateCardClick(date);
                }
              }}
            />
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="calendar-view__card-stack" ref={cardStackRef}>
      {renderContent()}
    </div>
  );
}

type AllDaysCardProps = Readonly<{
  totalJobs?: number;
  isSelected?: boolean;
  onClick?: () => void;
}>;

function AllDaysCard({ totalJobs, isSelected, onClick }: AllDaysCardProps) {
  return (
    <div className="calendar-view__all-card">
      <button className={clsx('date-card date-card--all', { 'date-card--selected': isSelected })} onClick={onClick}>
        <p className="date-card__date">All week</p>
        <p className="date-card__chip">{totalJobs} jobs</p>
      </button>
    </div>
  );
}
